<template>
  <div
    class="relative my-2 bg-gray-200 p-4 rounded-md sm:flex sm:justify-between items-center mr-2"
  >
    <div class="mb-4 sm:mb-0">
      <eva-icon
        class="cursor-pointer align-bottom"
        name="plus-circle"
        fill="#6C63FF"
        @click="dispatch('virtual-list', 'create-population', source.id)"
      />
      <eva-icon
        class="cursor-pointer align-bottom ml-1 bg-purple-500 rounded-sm"
        name="person"
        fill="#fff"
        @click="dispatch('virtual-list', 'contact-person', source.id)"
      />
      <a
        class="hover:text-purple-500 font-semibold ml-2"
        href="#"
        @click.prevent="dispatch('virtual-list', 'open-details', source.id)"
      >
        {{ source.name }}
      </a>
    </div>
    <div
      :id="`${source.id}-day-boxes`"
      class="flex overflow-x-auto days-container h-10"
    >
      <div
        v-for="day in days"
        :key="day"
        :class="`${parsedStyle(day, source.id)} text-white w-6 text-center flex-shrink-0 cursor-default`"
        @click="isToday(day) && alreadyReported(day, source.id) ? goToForm() : ''"
      >
        <div
          v-if="adminReport(day, source.id)"
          class="w-2 h-2 bg-purple-500 absolute rounded-full"
        />
        <div
          :ref="`day_${day}`"
          class="h-full flex flex-col justify-center"
          @mouseover="hoverEnter(day, source.id)"
          @mouseleave="hoverLeave"
        >
          {{ parsedDay(day) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment-timezone'
import { createPopper } from '@popperjs/core'

export default {
  name: 'PersonDetailsRow',
  props: {
    days: {
      type: Array,
      default: () => [],
    },
    todayWithSymptoms: {
      type: Array,
      default: () => [],
    },
    reportedToday: {
      type: Array,
      default: () => [],
    },
    index: {
      type: Number,
      default: 0,
    },
    instanceID: {
      type: String,
      default: '',
    },
    source: {
      type: Object,
      default: () => {},
    },
  },
  data () {
    return {
      popperInstance: null,
      tooltipElement: null,
      actualPersonID: null,
      actualDay: null,
      dayBoxes: null,
    }
  },
  computed: {
    symptomsOfPerson () {
      if (typeof this.lastDaysSymptoms[this.actualDay]?.riskyPersons !== 'undefined') {
        const answers = this.lastDaysSymptoms[this.actualDay]?.riskyPersons[this.actualPersonID].answers
        let parsedAnswers = []
        Object.keys(answers).forEach(categoryID => {
          parsedAnswers = [...parsedAnswers, ...answers[categoryID].labels]
        })
        return parsedAnswers
      }
      if (typeof this.lastDaysSymptoms[this.actualDay]?.symptoms !== 'undefined') {
        return this.lastDaysSymptoms[this.actualDay]?.symptoms[this.actualPersonID]
      }
      return []
    },
    ...mapState({
      persons: state => state.admin.persons,
      personsIndexes: state => state.admin.personsIndexes,
      lastDaysSymptoms: state => state.admin.lastDaysSymptoms,
    }),
  },
  watch: {
    symptomsOfPerson: function (val) {
      this.dispatch('virtual-list', 'change-symptoms', val)
    },
  },
  mounted () {
    this.tooltipElement = document.getElementById('symptomsTooltip')
  },
  beforeDestroy () {
    this.destroy()
  },
  methods: {
    create (el) {
      this.popperInstance = createPopper(el, this.tooltipElement, {
        placement: 'top',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 8],
            },
          }
        ],
      })
    },
    destroy () {
      if (this.popperInstance) {
        this.popperInstance.destroy()
        this.popperInstance = null
      }
    },
    hoverEnter (day, personID) {
      if (this.shouldShowTooltip(day, personID)) {
        this.actualPersonID = personID
        this.actualDay = day
        const el = this.$refs[`day_${day}`][0]
        this.tooltipElement.setAttribute('data-show', '')
        this.create(el)
      }
    },
    hoverLeave () {
      this.actualPersonID = null
      this.actualDay = null
      this.tooltipElement.removeAttribute('data-show')
      this.destroy()
    },
    dispatch (componentName, eventName, ...rest) {
      let parent = this.$parent || this.$root
      let name = parent.$options.name

      while (parent && (!name || name !== componentName)) {
        parent = parent.$parent
        if (parent) {
          name = parent.$options.name
        }
      }

      if (parent) {
        parent.$emit.apply(parent, [eventName].concat(rest))
      }
    },
    isToday (day) {
      return this.days.indexOf(day) === this.days.length - 1
    },
    adminReport (day, personID) {
      if (this.lastDaysSymptoms[day] && this.lastDaysSymptoms[day].adminReports && this.lastDaysSymptoms[day].adminReports.indexOf(personID) > -1) {
        return true
      }
      return false
    },
    parsedStyle (day, personID) {
      if (this.lastDaysSymptoms[day] && this.lastDaysSymptoms[day].reported.indexOf(personID) > -1) {
        if (this.lastDaysSymptoms[day].riskyPersons && this.lastDaysSymptoms[day].riskyPersons[personID]) {
          switch (this.lastDaysSymptoms[day].riskyPersons[personID].riskLevel) {
            case 1: return 'bg-yellow-500'
            case 2: return 'bg-orange-500'
            case 3: return 'bg-red-500'
          }
        }
        if (this.lastDaysSymptoms[day].symptoms && this.lastDaysSymptoms[day].symptoms[personID]) {
          return 'bg-red-500'
        }
        return 'bg-green-500'
      }
      if (this.isToday(day)) {
        return 'bg-gray-600 hover:bg-purple-500 cursor-pointer'
      }
      return 'bg-gray-400'
    },
    goToForm () {
      this.$f7router.navigate('/user/', {
        props: {
          forcedPersonID: this.source.id,
          forcedInstanceID: this.instanceID,
        },
      })
    },
    parsedDay (day) {
      return moment(day, 'x').add(5, 'hours').format('D')
    },
    alreadyReported (day, personID) {
      return this.lastDaysSymptoms[day] && !(this.lastDaysSymptoms[day].reported.indexOf(personID) > -1)
    },
    shouldShowTooltip (day, personID) {
      if (this.lastDaysSymptoms[day] && this.lastDaysSymptoms[day].reported.indexOf(personID) > -1) {
        if (this.lastDaysSymptoms[day].riskyPersons && this.lastDaysSymptoms[day].riskyPersons[personID]) {
          return true
        }
        if (this.lastDaysSymptoms[day].symptoms && this.lastDaysSymptoms[day].symptoms[personID]) {
          return true
        }
      }
      return false
    },
  },
}
</script>

<style lang="scss" scoped>
@import "../css/_brand";
</style>
